import { FilterValueType } from "./structured-search-schema";
import type { FilterValue } from "./types";
import { parseCostToNumber, parseLatencyToMs } from "./value-parsing";

const BETWEEN_OPERATOR = "between";
const RANGE_DISPLAY_SEPARATOR = " - ";

export interface ValueCoercionArgs {
  type: FilterValueType;
  operator: string;
  rawInput: string;
  value?: FilterValue;
}

export type ValueCoercionResult =
  | { value: FilterValue; valueDisplay?: string }
  | { error: string }
  | null;

const splitRangeParts = (rawInput: string): string[] =>
  rawInput.split(",").map(part => part.trim());

const parseNumericRange = (rawValue: FilterValue | undefined): [number, number] | null => {
  if (Array.isArray(rawValue) && rawValue.length >= 2) {
    const left = Number(rawValue[0]);
    const right = Number(rawValue[1]);
    return Number.isFinite(left) && Number.isFinite(right) ? [left, right] : null;
  }

  if (typeof rawValue !== "string") {
    return null;
  }

  const trimmed = rawValue.trim();
  if (!trimmed) {
    return null;
  }

  let parts: unknown[] | null = null;
  if (trimmed.startsWith("[")) {
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        parts = parsed;
      }
    } catch {
      return null;
    }
  } else if (trimmed.includes(",")) {
    parts = splitRangeParts(trimmed);
  }

  if (!parts || parts.length < 2) {
    return null;
  }

  const left = Number(parts[0]);
  const right = Number(parts[1]);
  return Number.isFinite(left) && Number.isFinite(right) ? [left, right] : null;
};

const coerceParsedRange = (
  rawInput: string,
  parse: (input: string) => number | null,
  errorMessage: string,
): ValueCoercionResult => {
  const parts = splitRangeParts(rawInput);
  if (parts.length >= 2) {
    const min = parse(parts[0] ?? "");
    const max = parse(parts[1] ?? "");
    if (min !== null && max !== null) {
      return {
        value: [min, max],
        valueDisplay: `${parts[0] ?? ""}${RANGE_DISPLAY_SEPARATOR}${parts[1] ?? ""}`,
      };
    }
  }
  return { error: errorMessage };
};

const coerceCost = (operator: string, rawInput: string, value: FilterValue | undefined) => {
  if (operator === BETWEEN_OPERATOR) {
    return coerceParsedRange(
      rawInput,
      parseCostToNumber,
      "Enter a valid cost range (e.g. 0.01, 0.10)",
    );
  }

  const parsed = parseCostToNumber(rawInput || String(value));
  if (parsed === null) {
    return { error: "Enter a valid cost (e.g. 0.01 or $0.01)" };
  }
  let valueDisplay: string | undefined;
  if (rawInput) {
    valueDisplay = rawInput.startsWith("$") ? rawInput : `$${rawInput}`;
  }
  return { value: parsed, valueDisplay };
};

const coerceLatency = (operator: string, rawInput: string, value: FilterValue | undefined) => {
  if (operator === BETWEEN_OPERATOR) {
    return coerceParsedRange(
      rawInput,
      parseLatencyToMs,
      "Enter a valid latency range (e.g. 100ms, 5s)",
    );
  }

  const parsed = parseLatencyToMs(rawInput || String(value));
  if (parsed === null) {
    return { error: "Enter a valid latency (e.g. 100ms or 5s)" };
  }
  return { value: parsed, valueDisplay: rawInput || undefined };
};

const coerceNumber = (operator: string, value: FilterValue | undefined): ValueCoercionResult => {
  if (operator !== BETWEEN_OPERATOR) {
    return null;
  }
  const parsed = parseNumericRange(value);
  if (!parsed) {
    return { error: "Enter a valid number range (e.g. 1, 100)" };
  }
  return { value: parsed };
};

/**
 * Centralized, type-driven completion coercion. Returns:
 * - `{ value, valueDisplay? }` when the raw value was parsed/normalized,
 * - `{ error }` when the value is invalid for the type,
 * - `null` when the type needs no completion coercion (value passes through).
 */
export const coerceFilterValue = ({
  type,
  operator,
  rawInput,
  value,
}: ValueCoercionArgs): ValueCoercionResult => {
  switch (type) {
    case FilterValueType.Cost:
      return coerceCost(operator, rawInput, value);
    case FilterValueType.Latency:
      return coerceLatency(operator, rawInput, value);
    case FilterValueType.Number:
      return coerceNumber(operator, value);
    default:
      return null;
  }
};
